import { Box } from "@mui/material";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { DataGridComponent } from "../common/DataGridComponent";
import { GridColDef, GridSortModel } from "@mui/x-data-grid-pro";
import { useSnackbar } from "../../hooks/useSnackbar";
import { debounce } from "lodash";
import { useAudit } from "../../hooks/useAudit";
import { formatDateTime } from "../../constants/dateFormats";

const LoginHistory: React.FunctionComponent<any> = () => {
  const [rowData, setRowData] = useState([]);

  const { handleError } = useSnackbar();
  const [rowsCount, setRowsCount] = useState<number>(0);
  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: 20,
  });
  const [filterObj, setFilterObject] = useState("");
  const {
    getModuleAudit,
    moduleAuditData,
    moduleAuditIsLoading,
    moduleAuditIsError,
    moduleAuditError,
  } = useAudit();
  const [sortModel, setSortModel] = useState<GridSortModel>([
    { field: "login_time", sort: "desc" },
  ]);
  const handleSortModelChange = useCallback(
    debounce((model: GridSortModel) => {
      setSortModel(model);
    }, 300),
    []
  );

  const getLoginHistory = (from: number, size: number, filterObj?: any) => {
    getModuleAudit({
      module: `LoginHistory`,
      from,
      size,
      value: filterObj && filterObj?.quickFilter ? filterObj?.quickFilter : "",
      sort_by: sortModel?.[0]?.field,
      sort_order: sortModel?.[0]?.sort,
      data: filterObj && filterObj?.advanceFilter,
    });
  };

  useEffect(() => {
    if (paginationModel?.pageSize) {
      const { page, pageSize } = paginationModel;
      if (page * pageSize > rowsCount - 1) {
        getLoginHistory(0, pageSize, filterObj);
      } else {
        getLoginHistory(page * pageSize, pageSize, filterObj);
      }
    }
  }, [paginationModel, filterObj]);

  useEffect(() => {
    if (moduleAuditData && moduleAuditData?.count > 0) {
      setRowsCount(moduleAuditData?.count);

      const data = moduleAuditData?.result?.map((ele: any) => {
        return {
          ...ele,
          id: ele.id,
        };
      });
      setRowData(data);
    } else if (moduleAuditData?.count == 0) {
      setRowsCount(0);
      setRowData([]);
    }
  }, [moduleAuditData]);

  useEffect(() => {
    if (moduleAuditIsError) {
      setRowData([]);
      setRowsCount(0);
      const error: any = moduleAuditError;
      handleError(error);
    }
  }, [moduleAuditIsError]);

  const columns: GridColDef[] = useMemo(
    () => [
      {
        field: "email",
        headerName: "Email",
        minWidth: 120,
        flex: 1,
      },
      {
        field: "login_role",
        headerName: "Role",
        minWidth: 120,
        flex: 1,
      },
      {
        field: "login_time",
        headerName: "Log In Time",
        minWidth: 120,
        flex: 1,
        type: "dateTime",
        valueFormatter: (value: any) => {
          return value || value !== null ? formatDateTime(value) : null;
        },
      },
      {
        field: "logout_time",
        headerName: "Log Out Time",
        minWidth: 120,
        flex: 1,
        type: "dateTime",
        valueFormatter: (value: any) => {
          return value || value !== null ? formatDateTime(value) : null;
        },
      },
      {
        field: "active_login_period",
        headerName: "Active Login Period",
        minWidth: 160,
        flex: 1,
        filterable: false,
        sortable: false,
      },
      {
        field: "status",
        headerName: "Status",
        align: "center",
        width: 70,
        renderCell: (params: any) => (
          <span
            className={`indicatorCircle ${params.row.logout_time && "grey"}`}
          />
        ),
        filterable: false,
        sortable: false,
      },
    ],
    []
  );
  return (
    <Box sx={{ width: "100%", height: "calc(100vh - 234px)" }}>
      <DataGridComponent
        columns={columns}
        rows={rowData ?? []}
        showQuickFilter
        paginationMode="server"
        rowsCount={rowsCount}
        paginationModel={paginationModel}
        onPaginationModelChange={setPaginationModel}
        loading={moduleAuditIsLoading}
        filterMode="server"
        filterValues={(data: any) => setFilterObject(data)}
        sortingMode="server"
        sortModel={sortModel}
        onSortModelChange={handleSortModelChange}
        exportFileName="Login_History"
        disableRowSelectionOnClick
      />
    </Box>
  );
};

export default LoginHistory;
